/**
 * Notes (Discord's, 2026-10-29): your private note on someone's profile.
 * Fetched when a profile opens (`GET /users/@me/notes/{id}`, a few bytes),
 * saved on blur (`PUT`), and kept in step with your other devices by
 * `USER_NOTE_UPDATE`. Nobody else ever sees them.
 */
import {
  normalizeNote,
  SocialRoutes,
  type GatewayDispatch,
  type Snowflake,
  type UserNote,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { api } from '../lib/api';
import { onGatewayEvent } from '../store/umEvents';

interface NotesState {
  /** Your note on each user you have looked at ('' = none). */
  notes: Record<Snowflake, string>;
  /** The profile whose note field should take the focus when it opens ("Add note"). */
  focus: Snowflake | null;
}

export const useNotes = create<NotesState>(() => ({ notes: {}, focus: null }));

const loading = new Map<Snowflake, Promise<void>>();

let listening = false;
/**
 * Follows your other devices once a note is known here. Registered on first
 * use, not when the module loads (umEvents may still be loading then).
 */
function listen(): void {
  if (listening) return;
  listening = true;
  onGatewayEvent(feedNotesFrame);
}

/** Loads your note on `userId` once (a profile opening). */
export function loadNote(userId: Snowflake): Promise<void> {
  listen();
  if (userId in useNotes.getState().notes) return Promise.resolve();
  let p = loading.get(userId);
  if (!p) {
    p = api.client
      .get<UserNote>(SocialRoutes.meNote(userId))
      .then((r) => setNote(userId, r.note ?? ''))
      .catch(() => undefined)
      .finally(() => loading.delete(userId));
    loading.set(userId, p);
  }
  return p;
}

function setNote(userId: Snowflake, note: string): void {
  useNotes.setState((s) => ({ notes: { ...s.notes, [userId]: note } }));
}

/**
 * Saves the note (trimmed; blank removes it). Shows it at once and puts the
 * old one back if the server refuses. Resolves false when it failed or was
 * too long.
 */
export async function saveNote(userId: Snowflake, raw: string): Promise<boolean> {
  const next = normalizeNote(raw);
  if (next === false) return false;
  listen();
  const before = useNotes.getState().notes[userId] ?? '';
  if ((next ?? '') === before) return true;
  setNote(userId, next ?? '');
  try {
    await api.client.put<void>(SocialRoutes.meNote(userId), { note: next ?? '' });
    return true;
  } catch {
    setNote(userId, before);
    return false;
  }
}

/** "Add note" from a menu: the profile's note field takes the focus. */
export function focusNoteOf(userId: Snowflake | null): void {
  useNotes.setState({ focus: userId });
}

/** Another device changed a note; a new session (READY) starts from the server again. */
export function feedNotesFrame(frame: GatewayDispatch): void {
  if (frame.t === 'USER_NOTE_UPDATE') setNote(frame.d.id, frame.d.note);
  else if (frame.t === 'READY') useNotes.setState({ notes: {}, focus: null });
}
